<template>
  <Toast />
  <div 
    v-if="isLoading && !isMobile" 
    class="app-content-container page-layout update-msg"
  >
    <ProgressSpinner />
    <p style="margin-top:10px"> 
      Loading the document ... 
    </p>
    <p>Please wait a moment</p>
  </div>
  <div 
    class="app-content-container page-layout"
  >
    <div class="page-inner-content">
      <MainTitle 
        title="Reader" 
      />
      
      <!-- Desktop View -->
      <div
        v-if="!isMobile"
        class="desktop-view"
      >
        <div 
          v-show="!isLoading" 
          class="action-bar"
        >
          <VoiceReaderControls
            :is-loading="vrIsLoading"
            :is-playing="vrIsPlaying"
            :is-reading="vrIsReading"
            :is-paused="vrIsPaused"
            :error="vrError"
            :weekly-limit-reached="vrIsWeeklyLimitReached && !$store.state.readerDocument?.is_sample"
            @play="onVoicePlay"
            @pause="vrPause"
            @resume="vrResume"
            @stop="vrStop"
          />
          <Button
            data-test="extract-vocab-btn"
            label="Extract Vocabulary"
            :disabled="isLoading"
            class="extract-button ns-btn-primary"
            @click="openExtractionDialog"
          >
            <template #icon>
              <img 
                src="@/assets/images/dictionary.png" 
                alt="Extract" 
                class="button-icon-image"
              >
            </template>
          </Button>
        </div>
        <div class="reader-content">
          <div v-if="!hideReaderContent">
            <DocumentReader 
              :is-loading="isLoading"
              :disable-hover-effects="showExtractionDialog || showExtractionResultsDialog"
              :vr-active-sentence-text="vrActiveSentenceText"
              :vr-active-raw-sentence-text="vrActiveRawSentenceText"
              :vr-active-polygon-indices="vrActivePolygonIndices"
              @update-loading-status="updateLoadingStatus"
              @update-vocabulary-list="updateVocabularyList"
              @start-vocabulary-check="startVocabularyCheck"
              @page-changed="updateCurrentPage"
              @open-summary="openSummaryTab"
              @hide-reader-content="setReaderContentHidden"
            />
          </div>
          <div 
            v-show="!isLoading"
            class="tabs-panel"
          >
            <!-- Tab Headers -->
            <div class="tabs-header">
              <button
                data-test="summary-tab"
                class="tab-btn"
                :class="{ 'active': activeTab === 'summary' }"
                @click="activeTab = 'summary'"
              >
                📋 Summary
              </button>
              <button
                class="tab-btn"
                :class="{ 'active': activeTab === 'vocabulary' }"
                @click="activeTab = 'vocabulary'"
              >
                📚 Vocabulary
              </button>
              <button
                data-test="questions-tab"
                class="tab-btn"
                :class="{ 'active': activeTab === 'comprehension' }"
                @click="activeTab = 'comprehension'"
              >
                ❓ Questions
              </button>
            </div>

            <!-- Tab Content -->
            <div class="tabs-content">
              <!-- Summary Tab -->
              <div
                v-show="activeTab === 'summary'"
                class="tab-pane summary-tab"
              >
                <div
                  v-if="summaryError"
                  class="summary-error"
                >
                  <p>{{ summaryError }}</p>
                  <button
                    class="retry-btn"
                    @click="generateSummary"
                  >
                    Retry
                  </button>
                </div>

                <div
                  v-else-if="summaryText"
                  class="summary-data"
                >
                  <!-- Summary Section -->
                  <div class="summary-section">
                    <div class="summary-section-header">
                      <h4>📝 Summary</h4>
                      <FeedbackThumbs
                        v-if="summaryId"
                        :feature-type="'summary'"
                        :target-id="summaryId"
                      />
                    </div>
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                      class="summary-text"
                      v-html="renderMarkdown(summaryText)"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                  </div>

                  <div
                    v-if="summaryLoading"
                    class="summary-loading"
                  >
                    <ProgressSpinner
                      style="width: 30px; height: 30px;"
                      stroke-width="8"
                      fill="transparent"
                      animation-duration=".5s"
                    />
                  </div>

                  <!-- Generate/Refresh Button -->
                  <button
                    v-if="!summaryLoading"
                    class="generate-btn"
                    @click="generateSummary"
                  >
                    🔄 {{ isMobile ? 'Refresh' : 'Refresh Summary' }}
                  </button>
                </div>

                <div
                  v-else-if="summaryLoading"
                  class="summary-loading"
                >
                  <ProgressSpinner 
                    style="width: 50px; height: 50px;" 
                    stroke-width="8" 
                    fill="transparent"
                    animation-duration=".5s" 
                  />
                  <p style="margin-top: 10px;">
                    Generating summary...
                  </p>
                </div>

                <div
                  v-else
                  class="summary-empty"
                >
                  <p>No summary generated yet</p>
                  <button
                    class="generate-btn"
                    data-test="generate-summary-btn"
                    @click="generateSummary"
                  >
                    ✨ Generate Summary
                  </button>
                </div>
              </div>

              <!-- Vocabulary Tab -->
              <div
                v-show="activeTab === 'vocabulary'"
                class="tab-pane vocabulary-tab"
              >
                <VocabularyExtractionDialog
                  :visible="showExtractionDialog"
                  :current-page="currentPage"
                  @hide="closeExtractionDialog"
                  @extract="handleExtractVocabulary"
                />
                <Dialog
                  v-model:visible="showExtractionResultsDialog"
                  :style="{ width: '600px' }"
                  header="Extraction Results"
                  :modal="true"
                  @hide="closeExtractionResultsDialog"
                >
                  <div
                    v-if="isLoadingExtraction"
                    class="loading-state"
                  >
                    <ProgressSpinner 
                      style="width: 50px; height: 50px;" 
                      stroke-width="8" 
                      fill="transparent"
                      animation-duration=".5s" 
                    />
                    <p style="margin-top: 10px;">
                      Extracting vocabulary...
                    </p>
                  </div>
                  <div v-else-if="extractedVocabularyList.length > 0">
                    <VocabularyExtractionResults
                      :vocabulary-list="extractedVocabularyList"
                      :resource-id="$store.state.readerDocument?.id"
                      @words-saved="handleWordsSaved"
                    />
                  </div>
                  <div
                    v-else
                    class="no-results"
                  >
                    <p>No vocabulary to display.</p>
                  </div>
                </Dialog>

                <div
                  v-if="isLoadingVocabulary"
                  class="loading-state"
                >
                  <ProgressSpinner 
                    style="width: 50px; height: 50px; margin-top: 30px;" 
                    stroke-width="8" 
                    fill="transparent"
                    animation-duration=".5s" 
                  />
                </div>
                <div
                  v-else-if="vocabularyDetailsList.length === 0"
                  class="empty-vocabulary"
                >
                  <p v-if="isUrlResource">
                    Use the Chrome Extension to look up vocabulary in the webpage
                  </p>
                  <p v-else-if="vocabularyLookupAttempted">
                    No vocabulary found for the selected text. Try selecting a different word or phrase.
                  </p>
                  <p v-else>
                    Select text in the document to extract vocabulary
                  </p>
                </div>
                <div v-else>
                  <div
                    v-for="(vocabularyGroup, index) in groupedVocabulary" 
                    :key="`vocab-group-${index}-${vocabularyGroup[0]?.word}`"
                  >
                    <VocabularyMeaningCard
                      :vocabulary-details="vocabularyGroup"
                      :group-index="index"
                    />
                  </div>
                </div>
              </div>

              <!-- Comprehension Questions Tab -->
              <div
                v-show="activeTab === 'comprehension'"
                class="tab-pane comprehension-tab"
              >
                <div
                  v-if="questionsError"
                  class="questions-error"
                >
                  <p>{{ questionsError }}</p>
                  <button
                    class="retry-btn"
                    @click="generateComprehensionQuestions(currentQuestionPhase)"
                  >
                    Retry
                  </button>
                </div>

                <div
                  v-else-if="questionsLoading || (currentQuestionPhase === 'before_reading' && comprehensionQuestions?.questions) || (currentQuestionPhase === 'after_reading' && comprehensionQuestionsAfterReading?.questions)"
                  class="questions-data"
                >
                  <div class="questions-header">
                    <h4>❓ {{ isMobile ? 'Comprehension' : 'Comprehension Questions' }}</h4>
                    <p class="generated-info">
                      {{ currentQuestionPhase === 'before_reading' ? 'Pre-reading' : 'Post-reading' }}
                      <span v-if="!isMobile && !questionsLoading">
                        - Generated: {{ new Date(currentQuestionPhase === 'before_reading' ? comprehensionQuestions?.generated_at : comprehensionQuestionsAfterReading?.generated_at).toLocaleString() }}
                      </span>
                    </p>
                  </div>

                  <div
                    v-for="(question, index) in questionsLoading ? streamingQuestions : (currentQuestionPhase === 'before_reading' ? comprehensionQuestions?.questions || [] : comprehensionQuestionsAfterReading?.questions || [])"
                    :key="question.id ?? index"
                    class="question-card"
                  >
                    <div class="question-header">
                      <span class="question-number">Q{{ index + 1 }}</span>
                      <span
                        class="question-difficulty"
                        :class="`difficulty-${question.difficulty_level}`"
                      >
                        {{ question.difficulty_level }}
                      </span>
                      <FeedbackThumbs
                        v-if="!questionsLoading && question.id"
                        class="question-feedback"
                        :feature-type="'comprehension_question'"
                        :target-id="question.id"
                      />
                    </div>

                    <p class="question-text">
                      {{ question.question }}
                    </p>

                    <div
                      v-if="question.question_type === 'multiple_choice' && question.options && question.options.length > 0"
                      class="options-container"
                    >
                      <div
                        v-for="(option, optIdx) in question.options"
                        :key="optIdx"
                        class="option-item"
                      >
                        {{ String.fromCharCode(65 + optIdx) }}. {{ option }}
                      </div>
                    </div>

                    <button
                      v-if="!questionsLoading"
                      class="show-answer-btn"
                      @click="toggleAnswerVisibility(question.id ?? index)"
                    >
                      {{ isMobile ? (expandedQuestions[question.id ?? index] ? 'Hide Answer' : 'Show Answer') : (expandedQuestions[question.id ?? index] ? '🔽 Hide Answer' : '▶️ Show Answer') }}
                    </button>

                    <div
                      v-if="!questionsLoading && expandedQuestions[question.id ?? index]"
                      class="answer-section"
                    >
                      <strong>Answer:</strong> {{ question.answer }}
                    </div>

                    <div
                      v-if="!questionsLoading && expandedQuestions[question.id ?? index]"
                      class="explanation-section"
                    >
                      <strong>Explanation:</strong> {{ question.explanation }}
                    </div>
                  </div>

                  <!-- Trailing spinner while streaming -->
                  <div
                    v-if="questionsLoading"
                    class="questions-loading"
                  >
                    <ProgressSpinner
                      style="width: 40px; height: 40px;"
                      stroke-width="8"
                      fill="transparent"
                      animation-duration=".5s"
                    />
                    <p style="margin-top: 8px;">
                      {{ streamingQuestions.length === 0 ? 'Generating comprehension questions...' : 'Loading more questions...' }}
                    </p>
                  </div>

                  <!-- Generate Post-reading Button -->
                  <button
                    v-if="!questionsLoading && currentQuestionPhase === 'before_reading' && !comprehensionQuestionsAfterReading && $store.state.readerDocument?.category !== 'textbook_listening'"
                    class="generate-btn"
                    @click="generateComprehensionQuestions('after_reading')"
                  >
                    ✨ {{ isMobile ? 'Post-reading' : 'Generate Post-reading Questions' }}
                  </button>
                </div>

                <div
                  v-else
                  class="questions-empty"
                >
                  <div class="difficulty-selector">
                    <label :for="isMobile ? 'mobile-difficulty-dropdown' : 'difficulty-dropdown'">Difficulty{{ isMobile ? ':' : ' Level:' }}</label>
                    <Select
                      :id="isMobile ? 'mobile-difficulty-dropdown' : 'difficulty-dropdown'"
                      v-model="selectedDifficulty"
                      :options="difficultyOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="Select"
                      class="difficulty-dropdown"
                    />
                  </div>
                  <div class="button-group">
                    <button
                      class="generate-btn"
                      data-test="generate-questions-btn"
                      :disabled="!selectedDifficulty"
                      @click="generateComprehensionQuestions('before_reading')"
                    >
                      ✨ Pre-reading
                    </button>
                    <button
                      v-if="$store.state.readerDocument?.category !== 'textbook_listening'"
                      class="generate-btn"
                      data-test="generate-questions-btn"
                      :disabled="!selectedDifficulty"
                      @click="generateComprehensionQuestions('after_reading')"
                    >
                      ✨ Post-reading
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Mobile View -->
      <div
        v-if="isMobile"
        class="mobile-view"
      >
        <div 
          class="mobile-button-container"
        >
          <Button
            label="Extract Vocabulary"
            class="mobile-action-btn ns-btn-primary"
            @click="openExtractionDialog"
          >
            <template #icon>
              <img 
                src="@/assets/images/dictionary.png" 
                alt="Extract" 
                class="button-icon-image"
              >
            </template>
          </Button>
        </div>

        <!-- Vocabulary Extraction Dialog -->
        <VocabularyExtractionDialog
          :visible="showExtractionDialog"
          :current-page="currentPage"
          @hide="closeExtractionDialog"
          @extract="handleExtractVocabulary"
        />

        <!-- Extraction Results Dialog -->
        <Dialog
          v-model:visible="showExtractionResultsDialog"
          :style="{ width: '90vw', maxWidth: '500px' }"
          header="Extraction Results"
          :modal="true"
          class="extraction-results-mobile"
          @hide="closeExtractionResultsDialog"
        >
          <div
            v-if="isLoadingExtraction"
            class="loading-state"
          >
            <ProgressSpinner 
              style="width: 50px; height: 50px;" 
              stroke-width="8" 
              fill="transparent"
              animation-duration=".5s" 
            />
            <p style="margin-top: 10px;">
              Extracting vocabulary...
            </p>
          </div>
          <div v-else-if="extractedVocabularyList.length > 0">
            <VocabularyExtractionResults
              :vocabulary-list="extractedVocabularyList"
              :resource-id="$store.state.readerDocument?.id"
              @words-saved="handleWordsSaved"
            />
          </div>
          <div
            v-else
            class="no-results"
          >
            <p>No vocabulary to display.</p>
          </div>
        </Dialog>
        <div 
          class="mobile-tabs-panel"
        >
          <!-- Tab Headers -->
          <div class="mobile-tabs-header">
            <button
              class="mobile-tab-btn"
              :class="{ 'active': activeTab === 'reader' }"
              @click="activeTab = 'reader'"
            >
              📄 Reader
            </button>
            <button
              class="mobile-tab-btn"
              :class="{ 'active': activeTab === 'summary' }"
              @click="activeTab = 'summary'"
            >
              📋 Summary
            </button>
            <button
              class="mobile-tab-btn"
              :class="{ 'active': activeTab === 'comprehension' }"
              @click="activeTab = 'comprehension'"
            >
              ❓ Questions
            </button>
          </div>

          <!-- Tab Content (Shared with desktop) -->
          <div class="mobile-tabs-content">
            <!-- Reader Tab -->
            <div
              v-show="activeTab === 'reader'"
              class="mobile-tab-pane mobile-reader-pane"
            >
              <div
                v-if="isLoading"
                class="loading-state"
              >
                <ProgressSpinner
                  style="width: 50px; height: 50px;"
                  stroke-width="8"
                  fill="transparent"
                  animation-duration=".5s"
                />
                <p style="margin-top: 10px;">
                  Loading the document...
                </p>
              </div>
              <DocumentReader
                :is-loading="isLoading"
                :disable-hover-effects="showExtractionDialog || showExtractionResultsDialog"
                :vr-active-sentence-text="vrActiveSentenceText"
                :vr-active-raw-sentence-text="vrActiveRawSentenceText"
                :vr-active-polygon-indices="vrActivePolygonIndices"
                @update-loading-status="updateLoadingStatus"
                @update-vocabulary-list="updateVocabularyList"
                @start-vocabulary-check="startVocabularyCheck"
                @page-changed="updateCurrentPage"
                @open-summary="activeTab = 'summary'"
                @hide-reader-content="setReaderContentHidden"
              />
            </div>

            <!-- Summary Tab -->
            <div
              v-show="activeTab === 'summary'"
              class="mobile-tab-pane"
            >
              <div
                v-if="summaryError"
                class="summary-error"
              >
                <p>{{ summaryError }}</p>
                <button
                  class="retry-btn"
                  @click="generateSummary"
                >
                  Retry
                </button>
              </div>

              <div
                v-else-if="summaryText"
                class="summary-data"
              >
                <div class="summary-section">
                  <div class="summary-section-header">
                    <h4>📝 Summary</h4>
                    <FeedbackThumbs
                      v-if="summaryId"
                      :feature-type="'summary'"
                      :target-id="summaryId"
                    />
                  </div>
                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    class="summary-text"
                    v-html="renderMarkdown(summaryText)"
                  />
                  <!-- eslint-enable vue/no-v-html -->
                </div>
                <div
                  v-if="summaryLoading"
                  class="summary-loading"
                >
                  <ProgressSpinner
                    style="width: 30px; height: 30px;"
                    stroke-width="8"
                    fill="transparent"
                    animation-duration=".5s"
                  />
                </div>
                <button
                  v-if="!summaryLoading"
                  class="generate-btn"
                  @click="generateSummary"
                >
                  🔄 Refresh
                </button>
              </div>

              <div
                v-else-if="summaryLoading"
                class="summary-loading"
              >
                <ProgressSpinner 
                  style="width: 50px; height: 50px;" 
                  stroke-width="8" 
                  fill="transparent"
                  animation-duration=".5s" 
                />
                <p style="margin-top: 10px;">
                  Generating summary...
                </p>
              </div>

              <div
                v-else
                class="summary-empty"
              >
                <p>No summary generated yet</p>
                <button
                  class="generate-btn"
                  @click="generateSummary"
                >
                  ✨ Generate Summary
                </button>
              </div>
            </div>

            <!-- Comprehension Questions Tab -->
            <div
              v-show="activeTab === 'comprehension'"
              class="mobile-tab-pane"
            >
              <div
                v-if="questionsError"
                class="questions-error"
              >
                <p>{{ questionsError }}</p>
                <button
                  class="retry-btn"
                  @click="generateComprehensionQuestions(currentQuestionPhase)"
                >
                  Retry
                </button>
              </div>

              <div
                v-else-if="questionsLoading || (currentQuestionPhase === 'before_reading' && comprehensionQuestions?.questions) || (currentQuestionPhase === 'after_reading' && comprehensionQuestionsAfterReading?.questions)"
                class="questions-data"
              >
                <div class="questions-header">
                  <h4>❓ Comprehension</h4>
                  <p class="generated-info">
                    {{ currentQuestionPhase === 'before_reading' ? 'Pre-reading' : 'Post-reading' }}
                  </p>
                </div>

                <div
                  v-for="(question, index) in questionsLoading ? streamingQuestions : (currentQuestionPhase === 'before_reading' ? comprehensionQuestions?.questions || [] : comprehensionQuestionsAfterReading?.questions || [])"
                  :key="question.id ?? index"
                  class="question-card"
                >
                  <div class="question-header">
                    <span class="question-number">Q{{ index + 1 }}</span>
                    <span
                      class="question-difficulty"
                      :class="`difficulty-${question.difficulty_level}`"
                    >
                      {{ question.difficulty_level }}
                    </span>
                    <FeedbackThumbs
                      v-if="!questionsLoading && question.id"
                      class="question-feedback"
                      :feature-type="'comprehension_question'"
                      :target-id="question.id"
                    />
                  </div>

                  <p class="question-text">
                    {{ question.question }}
                  </p>

                  <div
                    v-if="question.question_type === 'multiple_choice' && question.options && question.options.length > 0"
                    class="options-container"
                  >
                    <div
                      v-for="(option, optionIndex) in question.options"
                      :key="optionIndex"
                      class="option-item"
                    >
                      <strong>{{ String.fromCharCode(65 + optionIndex) }}.</strong> {{ option }}
                    </div>
                  </div>

                  <button
                    v-if="!questionsLoading"
                    class="show-answer-btn"
                    @click="toggleAnswerVisibility(question.id ?? index)"
                  >
                    {{ expandedQuestions[question.id ?? index] ? 'Hide Answer' : 'Show Answer' }}
                  </button>

                  <div
                    v-if="!questionsLoading && expandedQuestions[question.id ?? index]"
                    class="answer-section"
                  >
                    <strong>Answer:</strong> {{ question.answer }}
                  </div>

                  <div
                    v-if="!questionsLoading && expandedQuestions[question.id ?? index]"
                    class="explanation-section"
                  >
                    <strong>Explanation:</strong> {{ question.explanation }}
                  </div>
                </div>

                <!-- Trailing spinner while streaming -->
                <div
                  v-if="questionsLoading"
                  class="questions-loading"
                >
                  <ProgressSpinner
                    style="width: 40px; height: 40px;"
                    stroke-width="8"
                    fill="transparent"
                    animation-duration=".5s"
                  />
                  <p style="margin-top: 8px;">
                    {{ streamingQuestions.length === 0 ? 'Generating questions...' : 'Loading more questions...' }}
                  </p>
                </div>

                <button
                  v-if="!questionsLoading && currentQuestionPhase === 'before_reading' && !comprehensionQuestionsAfterReading && $store.state.readerDocument?.category !== 'textbook_listening'"
                  class="generate-btn"
                  @click="generateComprehensionQuestions('after_reading')"
                >
                  ✨ Post-reading
                </button>
              </div>

              <div
                v-else
                class="questions-empty"
              >
                <div class="difficulty-selector">
                  <label for="mobile-difficulty-dropdown">Difficulty:</label>
                  <Select
                    id="mobile-difficulty-dropdown"
                    v-model="selectedDifficulty"
                    :options="difficultyOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Select"
                    class="difficulty-dropdown"
                  />
                </div>
                <div class="button-group">
                  <button
                    class="generate-btn"
                    :disabled="!selectedDifficulty"
                    @click="generateComprehensionQuestions('before_reading')"
                  >
                    ✨ Pre-reading
                  </button>
                  <button
                    v-if="$store.state.readerDocument?.category !== 'textbook_listening'"
                    class="generate-btn"
                    :disabled="!selectedDifficulty"
                    @click="generateComprehensionQuestions('after_reading')"
                  >
                    ✨ Post-reading
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile vocabulary popup (bottom sheet) -->
        <div
          v-if="showVocabPopup"
          class="mobile-vocab-backdrop"
          @click.self="showVocabPopup = false"
        >
          <div class="mobile-vocab-sheet">
            <div class="mobile-vocab-sheet-header">
              <span>📚 Vocabulary</span>
              <button
                class="mobile-vocab-close-btn"
                @click="showVocabPopup = false"
              >
                ✕
              </button>
            </div>
            <div class="mobile-vocab-sheet-body">
              <div
                v-if="isLoadingVocabulary"
                class="loading-state"
              >
                <ProgressSpinner
                  style="width: 40px; height: 40px;"
                  stroke-width="8"
                  fill="transparent"
                  animation-duration=".5s"
                />
                <p style="margin-top: 8px;">
                  Looking up vocabulary...
                </p>
              </div>
              <div
                v-else-if="groupedVocabulary.length === 0"
                class="empty-vocabulary"
              >
                <p>No vocabulary found. Try selecting a different word.</p>
              </div>
              <div v-else>
                <div
                  v-for="(vocabularyGroup, groupIdx) in groupedVocabulary"
                  :key="`mobile-vocab-${groupIdx}-${vocabularyGroup[0]?.word}`"
                  class="mobile-vocab-card"
                >
                  <Carousel
                    :value="vocabularyGroup"
                    :num-visible="1"
                    :num-scroll="1"
                    :show-indicators="false"
                    :show-navigators="vocabularyGroup.length > 1"
                  >
                    <template #item="{ data: item }">
                      <div class="mobile-vocab-item">
                        <div class="mobile-vocab-word-row">
                          <span class="mobile-vocab-word">{{ item.word }}</span>
                          <span class="mobile-vocab-reading">{{ item.reading }}</span>
                          <span
                            v-if="item.jlpt?.length > 0"
                            class="mobile-vocab-jlpt"
                          >{{ item.jlpt.join(', ') }}</span>
                          <button
                            class="mobile-vocab-save-btn"
                            :class="{ 'saved': item.saved_word }"
                            :disabled="item.saved_word"
                            @click="saveMobileWord(item)"
                          >
                            <i :class="item.saved_word ? 'pi pi-bookmark-fill' : 'pi pi-bookmark'" />
                          </button>
                        </div>
                        <p class="mobile-vocab-meaning">
                          {{ item.english_meaning?.join(', ') }}
                        </p>
                      </div>
                    </template>
                  </Carousel>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile sticky voice player bar -->
        <div class="mobile-voice-bar">
          <VoiceReaderControls
            :is-loading="vrIsLoading"
            :is-playing="vrIsPlaying"
            :is-reading="vrIsReading"
            :is-paused="vrIsPaused"
            :error="vrError"
            :weekly-limit-reached="vrIsWeeklyLimitReached && !$store.state.readerDocument?.is_sample"
            @play="onVoicePlay"
            @pause="vrPause"
            @resume="vrResume"
            @stop="vrStop"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProgressSpinner from 'primevue/progressspinner';
import Toast from 'primevue/toast';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Select from 'primevue/select';
import Carousel from 'primevue/carousel';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import MainTitle from "@/components/title/MainTitle.vue";
import DocumentReader from "@/components/knowledgebase/DocumentReader.vue";
import VocabularyMeaningCard from '@/components/knowledgebase/VocabularyMeaningCard.vue';
import VocabularyExtractionDialog from '@/components/knowledgebase/VocabularyExtractionDialog.vue';
import VocabularyExtractionResults from '@/components/knowledgebase/VocabularyExtractionResults.vue';
import VoiceReaderControls from '@/components/knowledgebase/VoiceReaderControls.vue';
import FeedbackThumbs from '@/components/feedback/FeedbackThumbs.vue';
import { useVoiceReader } from '@/components/knowledgebase/composables/useVoiceReader';
import { postWithAuthentication } from '@/services/api';
import {
  BASE_URL,
  VOCABULARY_GENERATE_ENDPOINT,
  SAVE_WORD_ENDPOINT,
} from '@/assets/constants/api_endpoints';
import { StorageService } from '@/services/StorageService';
import { useReadingAssistance } from '@/components/knowledgebase/composables/useReadingAssistance';
import {
  WS_MESSAGE_SUMMARY_TOKEN,
  WS_MESSAGE_SUMMARY_DONE,
  WS_MESSAGE_QUESTION,
  WS_MESSAGE_QUESTIONS_DONE,
  WS_MESSAGE_ERROR,
  SAVE_WORD_SUCCESS_MSG,
  SAVE_WORD_FAILED_ERR_MSG,
  SUCCESS_STATUS,
  FAILED_STATUS,
} from '@/assets/constants/constants';

export default {
    name: "ResourceReader",
    components:{
        MainTitle,
        ProgressSpinner,
        Toast,
        Button,
        Dialog,
        Select,
        DocumentReader,
        VocabularyMeaningCard,
        VocabularyExtractionDialog,
        VocabularyExtractionResults,
        VoiceReaderControls,
        FeedbackThumbs,
        Carousel,
    },
    setup() {
        const {
            initializeSocket: initReadingSocket,
            setupSocketHandlers: setupReadingSocketHandlers,
            sendSummarizeRequest,
            sendGenerateQuestionsRequest,
            waitForSocketReady: waitForReadingSocketReady,
            closeSocket: closeReadingSocket,
        } = useReadingAssistance();

        const {
            isLoading: vrIsLoading,
            isPlaying: vrIsPlaying,
            isReading: vrIsReading,
            isPaused: vrIsPaused,
            isActive: vrIsActive,
            error: vrError,
            isWeeklyLimitReached: vrIsWeeklyLimitReached,
            activeSentenceText: vrActiveSentenceText,
            activeRawSentenceText: vrActiveRawSentenceText,
            activePolygonIndices: vrActivePolygonIndices,
            initializeSocket: initializeVrSocket,
            closeSocket: closeVrSocket,
            readPage: vrReadPage,
            pause: vrPause,
            resume: vrResume,
            stop: vrStop,
        } = useVoiceReader();

        return {
            initReadingSocket,
            setupReadingSocketHandlers,
            sendSummarizeRequest,
            sendGenerateQuestionsRequest,
            waitForReadingSocketReady,
            closeReadingSocket,
            vrIsLoading,
            vrIsPlaying,
            vrIsReading,
            vrIsPaused,
            vrIsActive,
            vrError,
            vrIsWeeklyLimitReached,
            vrActiveSentenceText,
            vrActiveRawSentenceText,
            vrActivePolygonIndices,
            initializeVrSocket,
            closeVrSocket,
            vrReadPage,
            vrPause,
            vrResume,
            vrStop,
        };
    },
    data() {
        return {
            isLoading: true,
            isLoadingVocabulary: false,
            showVocabPopup: false,
            vocabGeneration: 0,
            vocabularyDetailsList: [],
            vocabularyLookupAttempted: false,
            showExtractionDialog: false,
            showExtractionResultsDialog: false,
            isLoadingExtraction: false,
            extractedVocabularyList: [],
            currentPage: 1,
            activeTab: window.innerWidth <= 599 ? 'reader' : 'vocabulary',
            summaryText: '',
            summaryId: null,
            summaryLoading: false,
            summaryError: null,
            comprehensionQuestions: null,
            comprehensionQuestionsAfterReading: null,
            streamingQuestions: [],
            questionsLoading: false,
            questionsError: null,
            selectedDifficulty: null,
            currentQuestionPhase: 'before_reading',
            difficultyOptions: [
                { label: 'Easy', value: 'easy' },
                { label: 'Intermediate', value: 'intermediate' },
                { label: 'Hard', value: 'hard' }
            ],
            expandedQuestions: {},
            hideReaderContent: false,
            pendingVocabulary: [],
            vocabularyDebounceTimer: null,
        }
    },
    computed: {
        isUrlResource() {
            return this.$store.state.readerDocument?.resource_type === 'url';
        },
        isMobile() {
            return window.innerWidth <= 599;
        },
        groupedVocabulary() {
            const grouped = {};
            this.vocabularyDetailsList.forEach(vocab => {
                const key = vocab.token || 'unknown';
                if (!grouped[key]) {
                    grouped[key] = [];
                }
                grouped[key].push(vocab);
            });
            return Object.values(grouped).map(group => [...group].reverse());
        }
    },
    mounted() {
        console.log("ResourceReader mounted. Current resource:", this.$store.state.readerDocument);
        // Initialize reading assistance WebSocket
        const authToken = localStorage.getItem('authToken');
        this.initReadingSocket(authToken);
        this.setupReadingAssistanceHandlers();
        // Initialize voice reader WebSocket
        this.initializeVrSocket(authToken);
        // Restore resource document from localStorage if not already set in store
        if (!this.$store.state.readerDocument) {
            const resourceContext = StorageService.getResourceContext();
            if (resourceContext) {
                this.$store.state.readerDocument = resourceContext;
            } else {
                // No resource context found - redirect to knowledge base
                this.$toast.add({
                    severity: 'warn',
                    summary: 'No Resource Selected',
                    detail: 'Please select a resource to read. Redirecting...',
                    life: 2000,
                });
                setTimeout(() => {
                    this.$router.push('/knowledgebase');
                }, 2000);
            }
        }
        // Listen for window resize to update mobile state
        window.addEventListener('resize', this.handleWindowResize);
    },

    beforeUnmount() {
        // Remove resize listener
        window.removeEventListener('resize', this.handleWindowResize);
        clearTimeout(this.vocabularyDebounceTimer);
        this.closeReadingSocket();
        this.closeVrSocket();
        this.vrStop();
    },
    methods: {
        renderMarkdown(markdown) {
            if (!markdown) return '';
            const html = marked(markdown);
            return DOMPurify.sanitize(html);
        },
        handleWindowResize() {
            this.$forceUpdate();
        },

        updateLoadingStatus(newStatus) {
            this.isLoading = newStatus;
        },
        updateVocabularyList(newVocabulary) {
          const generation = this.vocabGeneration;
          if (newVocabulary !== null && newVocabulary !== undefined) {
            // Buffer items and flush only after the stream goes quiet (600 ms).
            // This prevents the grouped list from re-sorting mid-render, which
            // would shift data-test indices before the demo highlight attaches.
            this.pendingVocabulary.push(newVocabulary);
            clearTimeout(this.vocabularyDebounceTimer);
            this.vocabularyDebounceTimer = setTimeout(() => {
              // Discard if a newer lookup has started since this debounce was queued
              if (this.vocabGeneration !== generation) return;
              this.vocabularyDetailsList = [...this.vocabularyDetailsList, ...this.pendingVocabulary];
              this.pendingVocabulary = [];
              this.isLoadingVocabulary = false;
            }, 600);
          } else {
            // null signals an error/timeout — flush whatever was buffered
            clearTimeout(this.vocabularyDebounceTimer);
            if (this.pendingVocabulary.length > 0) {
              this.vocabularyDetailsList = [...this.vocabularyDetailsList, ...this.pendingVocabulary];
              this.pendingVocabulary = [];
            }
            this.isLoadingVocabulary = false;
          }
        },
        async saveMobileWord(vocabulary) {
          try {
            let jlpt = '';
            if (vocabulary.jlpt?.length > 0) jlpt = vocabulary.jlpt[0];
            let definition = [];
            if (vocabulary.english_meaning?.length > 0) definition = vocabulary.english_meaning[0];
            if (!vocabulary.word) vocabulary.word = vocabulary.reading;
            const resource = this.$store.state.readerDocument || {};
            console.log("Saving word:", vocabulary.word, "Definition:", definition, "JLPT:", jlpt, "Resource ID:", resource.id, "Subject ID:", resource.subject_id, "sample:", resource.is_sample);
            await postWithAuthentication(BASE_URL, SAVE_WORD_ENDPOINT, {
              word: vocabulary.word,
              definition: Array.isArray(definition) ? definition.join(', ') : definition,
              jlpt,
              reading: vocabulary.reading,
              resource_id: resource.id.toString(),
              subject_id: resource.subject_id?.toString(),
              is_sample: resource.is_sample,
            });
            vocabulary.saved_word = true;
            this.$toast.add({ severity: SUCCESS_STATUS, summary: SAVE_WORD_SUCCESS_MSG, life: 3000 });
          } catch {
            this.$toast.add({ severity: FAILED_STATUS, summary: SAVE_WORD_FAILED_ERR_MSG, life: 3000 });
          }
        },

        startVocabularyCheck(){
          this.vocabGeneration++;
          clearTimeout(this.vocabularyDebounceTimer);
          this.pendingVocabulary = [];
          this.vocabularyDetailsList = [];
          this.isLoadingVocabulary = true;
          this.vocabularyLookupAttempted = true;
          if (this.isMobile) {
            this.showVocabPopup = true;
          } else {
            this.activeTab = 'vocabulary';
          }
        },
        openExtractionDialog() {
          this.showExtractionDialog = true;
        },
        closeExtractionDialog() {
          this.showExtractionDialog = false;
        },
        async handleExtractVocabulary(settings) {
          this.closeExtractionDialog();
          this.isLoadingExtraction = true;
          this.extractedVocabularyList = [];
          this.showExtractionResultsDialog = true;

          try {
            const resource = this.$store.state.readerDocument || {};
            const payload = {
              resource_id: resource.id.toString(),
              page: this.currentPage,
              level: settings.level,
              topic: settings.topic,
              is_sample: resource.is_sample,
            };

            const response = await postWithAuthentication(
              BASE_URL,
              VOCABULARY_GENERATE_ENDPOINT,
              payload
            );

            // Handle response - extract vocabulary_list from response
            let vocabularyData = [];
            if (response && response.vocabulary_list && Array.isArray(response.vocabulary_list)) {
              vocabularyData = response.vocabulary_list;
            } else if (Array.isArray(response)) {
              vocabularyData = response;
            } else if (response && response.data && Array.isArray(response.data)) {
              vocabularyData = response.data;
            }

            this.extractedVocabularyList = vocabularyData;
            
            if (this.extractedVocabularyList.length === 0) {
              this.$toast.add({
                severity: 'info',
                summary: 'Info',
                detail: 'No vocabulary found for the selected criteria.',
                life: 3000,
              });
            }
          } catch (error) {
            console.error('Error extracting vocabulary:', error);
            this.$toast.add({
              severity: 'error',
              summary: 'Error',
              detail: error.message || 'Failed to extract vocabulary. Please try again.',
              life: 3000,
            });
          } finally {
            this.isLoadingExtraction = false;
          }
        },
        closeExtractionResultsDialog() {
          this.showExtractionResultsDialog = false;
          this.extractedVocabularyList = [];
        },
        handleWordsSaved(result) {
          // Close the dialog after saving
          this.closeExtractionResultsDialog();
        },
        updateCurrentPage(newPage) {
          this.currentPage = newPage;
          this.vrStop();
        },
        toggleAnswerVisibility(questionId) {
          this.expandedQuestions[questionId] = !this.expandedQuestions[questionId];
        },
        openSummaryTab() {
          this.activeTab = 'summary';
        },
        setReaderContentHidden() {
          this.hideReaderContent = true;
        },
        onVoicePlay() {
          const resource = this.$store.state.readerDocument;
          if (!resource) return;
          const pageNumber = resource.resource_type === 'pdf' ? this.currentPage : null;
          this.vrReadPage(resource.id, resource.is_sample, pageNumber);
        },
        setupReadingAssistanceHandlers() {
          this.setupReadingSocketHandlers(
            (message) => this.handleReadingAssistanceMessage(message),
            (error) => console.error('Reading assistance WS error:', error),
            () => {}
          );
        },
        handleReadingAssistanceMessage(message) {
          if (message.type === WS_MESSAGE_SUMMARY_TOKEN) {
            if (this.summaryLoading) this.summaryLoading = false;
            this.summaryText = message.token ?? '';
            const tokenId = message.summary_id ?? message.id ?? null;
            if (tokenId != null) this.summaryId = tokenId;
          } else if (message.type === WS_MESSAGE_SUMMARY_DONE) {
            // Cache hit: server sends full summary in one message
            const cacheText = message.summary ?? message.text ?? message.content ?? null;
            if (cacheText) {
              this.summaryText = cacheText;
            }
            const doneId = message.summary_id ?? message.id ?? null;
            if (doneId != null) this.summaryId = doneId;
            this.summaryLoading = false;
          } else if (message.type === WS_MESSAGE_QUESTION) {
            const normalize = (q) => ({
              ...q,
              difficulty_level: q.difficulty_level || q.question_difficulty,
            });
            const incoming = message.questions ?? message.question ?? message.data;
            if (Array.isArray(incoming)) {
              this.streamingQuestions.push(...incoming.map(normalize));
            } else if (incoming && typeof incoming === 'object') {
              this.streamingQuestions.push(normalize(incoming));
            }
          } else if (message.type === WS_MESSAGE_QUESTIONS_DONE) {
            
            // Normalize question difficulty_level
            const normalize = (q) => ({
              ...q,
              difficulty_level: q.difficulty_level || q.question_difficulty,
            });
            
            // Check if questions are in the message itself (from cache or all-at-once delivery)
            let questionsToUse = this.streamingQuestions;
            if (message.questions && Array.isArray(message.questions) && message.questions.length > 0) {
              questionsToUse = message.questions.map(normalize);
            } else {
              questionsToUse = this.streamingQuestions.map(normalize);
            }
            
            const built = {
              questions: questionsToUse,
              total_count: message.total_count ?? questionsToUse.length,
              resource_id: this.$store.state.readerDocument?.id,
              generated_at: new Date().toISOString(),
            };
            if (this._pendingQuestionsPhase === 'before_reading') {
              this.comprehensionQuestions = built;
            } else if (this._pendingQuestionsPhase === 'after_reading') {
              this.comprehensionQuestionsAfterReading = built;
            } else {
              console.warn('Unknown question phase:', this._pendingQuestionsPhase);
            }
            this.streamingQuestions = [];
            this.questionsLoading = false;
            this.currentQuestionPhase = this._pendingQuestionsPhase;
          } else if (message.type === WS_MESSAGE_ERROR) {
            if (this.summaryLoading) {
              this.summaryError = message.message || 'Failed to generate summary';
              this.summaryLoading = false;
            } else if (this.questionsLoading) {
              this.questionsError = message.message || 'Failed to generate comprehension questions';
              this.questionsLoading = false;
              this.streamingQuestions = [];
            }
          }
        },
        async generateSummary() {
          if (!this.$store.state.readerDocument || !this.$store.state.readerDocument.id) {
            this.summaryError = 'No document loaded';
            return;
          }

          this.summaryText = '';
          this.summaryLoading = true;
          this.summaryError = null;

          const resource = this.$store.state.readerDocument;
          await this.waitForReadingSocketReady();
          const sent = this.sendSummarizeRequest(resource.id, resource.is_sample);
          if (!sent) {
            this.summaryError = 'Connection unavailable. Please try again.';
            this.summaryLoading = false;
          }
        },
        async generateComprehensionQuestions(phase = 'before_reading') {
          if (!this.$store.state.readerDocument || !this.$store.state.readerDocument.id) {
            this.questionsError = 'No document loaded';
            return;
          }

          this.streamingQuestions = [];
          this.questionsLoading = true;
          this.questionsError = null;
          this.currentQuestionPhase = phase;
          this._pendingQuestionsPhase = phase;

          const resource = this.$store.state.readerDocument;
          await this.waitForReadingSocketReady();
          const sent = this.sendGenerateQuestionsRequest(
            resource.id,
            resource.is_sample,
            this.selectedDifficulty,
            phase
          );
          if (!sent) {
            this.questionsError = 'Connection unavailable. Please try again.';
            this.questionsLoading = false;
          }
        },
    },
}

</script>

<style scoped>
.page-layout {
    display: flex;
    font-family: var(--font-family);
    gap: 0;
    height: 90%;
    flex: 1;
    flex-direction: column;
    margin-top: 45px;
}

.page-inner-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    width: 100%;
    min-height: 0;
    padding-left: 20px;
}

.reader-header-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.action-bar {
    padding: 12px 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    background-color: transparent;
    margin-bottom: 10px;
}

/* kept for any legacy references */
.extract-button-container {
    padding: 15px 0;
    padding-right: 20px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    background-color: transparent;
    border-bottom: none;
    margin-bottom: 10px;
}

.reader-content {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 0;
    width: 100%;
    margin: 0 auto;
    padding: 0 20px;
    height: calc(100vh - 45px - 80px - 30px);
    overflow: hidden;
}

.reader-content > div:first-of-type {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: stretch;
    min-width: 0;
    min-height: 0;
}

.extract-button {
    white-space: nowrap;
    padding: 0.4rem 0.8rem !important;
    font-size: 0.875rem !important;
    margin-left: auto;
}

.update-msg {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 400px;
}

/* Tabs Panel */
@property --border-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
}

@keyframes rotate-border {
    to { --border-angle: 360deg; }
}

.tabs-panel {
    width: 400px;
    flex: 0 0 400px;
    border: 2px solid transparent;
    border-radius: var(--radius-md);
    background-image:
        linear-gradient(var(--color-surface), var(--color-surface)),
        conic-gradient(from var(--border-angle), var(--color-aurora-start), var(--color-aurora-end), var(--color-aurora-start));
    background-origin: border-box;
    background-clip: padding-box, border-box;
    animation: rotate-border 6s linear infinite;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-md);
    z-index: 99;
    min-height: 0;
    overflow: hidden;
}

.tabs-panel:only-child {
    flex: 1;
    width: 100%;
    border-left: none;
}

/* Tab Headers */
.tabs-header {
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(27, 30, 100, 0.10);
    background: var(--color-surface-raised);
    padding: 0;
}

.tab-btn {
    flex: 1;
    padding: 15px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
    font-family: var(--font-family);
    color: var(--color-text-secondary);
    border-bottom: 3px solid transparent;
    transition: all var(--transition-base);
    white-space: nowrap;
}

.tab-btn:hover {
    background-color: rgba(27, 30, 100, 0.05);
}

.tab-btn.active {
    color: var(--color-primary-mid);
    border-bottom-color: var(--color-primary-mid);
}

/* Tab Content */
.tabs-content {
    flex: 1;
    overflow-y: scroll;
    overflow-x: hidden;
    padding: 20px;
    width: 100%;
    display: flex;
    flex-direction: column;
    scrollbar-gutter: stable;
    box-sizing: border-box;
}

.tab-pane,
.mobile-tab-pane {
    animation: fadeIn 0.3s ease;
    width: 100%;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

/* Summary Tab Styles */
.summary-loading,
.summary-error,
.summary-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    color: #666;
}

.summary-error {
    color: #d32f2f;
}

.summary-loading p,
.summary-error p,
.summary-empty p {
    margin: 10px 0;
    font-size: 14px;
}

.summary-data {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.summary-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.summary-section h4,
.main-points-section h4 {
    margin: 0;
    font-size: 16px;
    color: #333;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
}

.summary-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #555;
    text-align: justify;
}

.summary-text p {
    margin: 10px 0;
    font-size: 14px;
    line-height: 1.6;
    color: #555;
}

.summary-text h1,
.summary-text h2,
.summary-text h3,
.summary-text h4,
.summary-text h5,
.summary-text h6 {
    margin: 15px 0 10px 0;
    font-weight: 600;
    color: #333;
    line-height: 1.4;
}

.summary-text h1 { font-size: 18px; }
.summary-text h2 { font-size: 16px; }
.summary-text h3 { font-size: 15px; }
.summary-text h4 { font-size: 14px; }
.summary-text h5 { font-size: 13px; }
.summary-text h6 { font-size: 12px; }

.summary-text ul,
.summary-text ol {
    margin: 10px 0;
    padding-left: 25px;
    color: #555;
}

.summary-text li {
    margin: 6px 0;
    font-size: 14px;
    line-height: 1.5;
}

.summary-text strong {
    font-weight: 600;
    color: #333;
}

.summary-text em {
    font-style: italic;
    color: #666;
}

.summary-text blockquote {
    margin: 12px 0;
    padding-left: 15px;
    border-left: 3px solid var(--color-aurora-start);
    background-color: rgba(69, 74, 170, 0.05);
    color: var(--color-text-secondary);
    font-style: italic;
}

.summary-text code {
    background-color: #f5f5f5;
    border-radius: 3px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    color: #e8403f;
}

.summary-text pre {
    background-color: #f5f5f5;
    border-radius: 6px;
    padding: 12px;
    overflow-x: auto;
    margin: 12px 0;
}

.summary-text pre code {
    background-color: transparent;
    color: #333;
    padding: 0;
    font-size: 12px;
}

.main-points-list {
    margin: 0;
    padding-left: 20px;
    list-style: none;
}

.main-point-item {
    font-size: 14px;
    color: #555;
    line-height: 1.6;
    margin-bottom: 10px;
    padding-left: 20px;
    position: relative;
}

.main-point-item::before {
    content: '→';
    position: absolute;
    left: 0;
    color: var(--color-aurora-start);
    font-weight: bold;
}

/* Vocabulary Tab Styles */
.empty-vocabulary {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #666;
    font-size: 14px;
}

/* Buttons */
.generate-btn,
.retry-btn {
    padding: var(--space-2) var(--space-8);
    background-color: var(--color-btn-primary-bg);
    color: var(--color-btn-primary-text);
    border: 1.5px solid var(--color-btn-primary-border);
    border-radius: var(--radius-md);
    font-family: var(--font-family);
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
    letter-spacing: 0.02em;
    cursor: pointer;
    width: fit-content;
    margin-top: var(--space-2);
    align-self: center;
    transition: background-color var(--transition-fast), box-shadow var(--transition-fast);
}

.generate-btn:hover,
.retry-btn:hover {
    background-color: var(--color-btn-primary-hover);
    box-shadow: var(--shadow-sm);
}

.generate-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Loading States */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
}

.no-results {
    padding: 1rem;
    text-align: center;
    color: #666;
}

.button-icon-image {
    width: 25px;
    height: 25px;
    margin-right: 0.5rem;
}

/* Scrollbar Styling */
.tabs-content::-webkit-scrollbar {
    width: 6px;
}

.tabs-content::-webkit-scrollbar-track {
    background: var(--color-surface-raised);
}

.tabs-content::-webkit-scrollbar-thumb {
    background: var(--color-aurora-start);
    border-radius: 3px;
}

.tabs-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-aurora-end);
}

.reader-content > :not(.tabs-panel) {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: stretch;
    min-width: 0;
    min-height: 0;
}

/* Comprehension Questions Tab Styles */
.comprehension-tab {
    min-height: 100%;
}

.questions-loading,
.questions-error,
.questions-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    text-align: center;
    color: #666;
}

.questions-error {
    color: #d32f2f;
}

.questions-loading p,
.questions-error p,
.questions-empty p {
    margin: 10px 0;
    font-size: 14px;
}

.questions-data {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.questions-header {
    margin-bottom: 10px;
}

.questions-header h4 {
    margin: 0 0 8px 0;
    font-size: 16px;
    color: #333;
    font-weight: 600;
}

.generated-info {
    margin: 0;
    font-size: 12px;
    color: #999;
}

.question-card {
    border: 1px solid rgba(27, 30, 100, 0.10);
    border-radius: var(--radius-md);
    padding: var(--space-4);
    background: var(--color-surface);
    margin-bottom: var(--space-4);
    transition: border-color var(--transition-base), box-shadow var(--transition-base);
}

.question-card:hover {
    border-color: var(--color-aurora-start);
    background: var(--color-surface-raised);
    box-shadow: var(--shadow-sm);
}

.question-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.question-feedback {
    margin-left: auto;
}

.question-number {
    font-weight: var(--font-weight-bold);
    color: var(--color-primary-mid);
    font-size: var(--font-size-sm);
    background: var(--color-surface-raised);
    padding: 4px 8px;
    border-radius: var(--radius-sm);
}

.question-difficulty {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    text-transform: capitalize;
}

.difficulty-easy {
    background: #e8f5e9;
    color: #2e7d32;
}

.difficulty-intermediate {
    background: #fff3e0;
    color: #e65100;
}

.difficulty-hard {
    background: #ffebee;
    color: #c62828;
}

.question-text {
    margin: 10px 0;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    line-height: 1.5;
}

.options-container {
    margin: var(--space-4) 0;
    padding: var(--space-3);
    background: linear-gradient(90deg, rgba(69, 74, 170, 0.05) 0%, transparent 60%);
    border-left: 3px solid var(--color-aurora-start);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}

.option-item {
    font-size: 13px;
    color: #555;
    margin-bottom: 8px;
    line-height: 1.4;
}

.option-item:last-child {
    margin-bottom: 0;
}

.show-answer-btn {
    padding: var(--space-2) var(--space-4);
    background-color: var(--color-btn-secondary-bg);
    color: var(--color-btn-secondary-text);
    border: none;
    border-radius: var(--radius-md);
    font-family: var(--font-family);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-regular);
    cursor: pointer;
    margin: var(--space-3) 0;
    width: fit-content;
    transition: background-color var(--transition-fast);
}

.show-answer-btn:hover {
    background-color: var(--color-btn-secondary-hover);
}

.answer-section {
    margin: var(--space-3) 0;
    padding: var(--space-3);
    background: var(--color-surface-raised);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    line-height: var(--line-height-base);
}

.answer-section strong {
    color: var(--color-primary-mid);
    display: block;
    margin-bottom: 5px;
}

.explanation-section {
    margin: var(--space-3) 0;
    padding: var(--space-3);
    background: rgba(69, 74, 170, 0.05);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    line-height: var(--line-height-base);
}

.explanation-section strong {
    color: var(--color-aurora-start);
    display: block;
    margin-bottom: 5px;
}

/* Difficulty Selector */
.difficulty-selector {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    justify-content: center;
}

.difficulty-selector label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}

.difficulty-dropdown {
    width: 150px;
}

/* Button Group */
.button-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    align-items: center;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .tabs-panel {
        width: 350px;
    }
}

@media (max-width: 768px) {
    .tabs-panel {
        position: fixed;
        bottom: 0;
        right: 0;
        top: auto;
        left: 0;
        width: 100%;
        height: 50vh;
        border-radius: var(--radius-md) var(--radius-md) 0 0;
    }
}

/* Mobile View Styles */
.mobile-view {
    display: none;
    flex-direction: column;
    width: 100%;
    flex: 1;
    min-height: 0;
    /* make room for sticky voice bar */
    padding-bottom: 72px;
    box-sizing: border-box;
}

/* Sticky voice reader bar — always visible at the bottom of the viewport on mobile */
.mobile-voice-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-top: 1.5px solid rgba(69, 74, 170, 0.20);
    box-shadow: 0 -2px 16px rgba(69, 74, 170, 0.10);
}

.mobile-button-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    background-color: #fff;
    border-bottom: 1px solid #e0e0e0;
}

.mobile-action-btn {
    width: 100%;
    padding: 12px !important;
    font-size: 14px !important;
    font-weight: 500 !important;
}

.mobile-tabs-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: white;
    min-height: 0;
    overflow: hidden;
}

.mobile-tabs-header {
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(27, 30, 100, 0.10);
    background: var(--color-surface-raised);
    padding: 0;
}

.mobile-tab-btn {
    flex: 1;
    padding: 12px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    font-family: var(--font-family);
    color: var(--color-text-secondary);
    border-bottom: 3px solid transparent;
    transition: all var(--transition-base);
    white-space: nowrap;
}

.mobile-tab-btn:hover {
    background-color: rgba(27, 30, 100, 0.05);
}

.mobile-tab-btn.active {
    color: var(--color-primary-mid);
    border-bottom-color: var(--color-primary-mid);
}

.mobile-tabs-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 15px;
    width: 100%;
    display: flex;
    flex-direction: column;
    scrollbar-gutter: stable;
    box-sizing: border-box;
}

.mobile-tab-pane {
    animation: fadeIn 0.3s ease;
    width: 100%;
}

.mobile-reader-pane {
    padding: 0;
    overflow: hidden;
    margin: -15px;
    width: calc(100% + 30px);
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.mobile-reader-pane .document-reader-container {
    flex: 1;
    min-height: 0;
    height: 100%;
    width: 100%;
}

.desktop-view {
    display: flex;
    flex-direction: column;
    flex: 1;
}

/* Mobile vocabulary bottom sheet */
.mobile-vocab-backdrop {
    position: fixed;
    inset: 0;
    z-index: 300;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
}

.mobile-vocab-sheet {
    width: 100%;
    max-height: 60vh;
    background: white;
    border-radius: 16px 16px 0 0;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
    padding-bottom: env(safe-area-inset-bottom);
    z-index: 301;
}

.mobile-vocab-sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 10px;
    border-bottom: 1px solid rgba(27, 30, 100, 0.10);
    font-weight: 600;
    font-size: 15px;
}

.mobile-vocab-close-btn {
    background: none;
    border: none;
    font-size: 18px;
    color: #666;
    cursor: pointer;
    padding: 4px 8px;
    line-height: 1;
}

.mobile-vocab-sheet-body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
}

.mobile-vocab-sheet-body :deep(.p-carousel),
.mobile-vocab-sheet-body :deep(.p-carousel-content),
.mobile-vocab-sheet-body :deep(.p-carousel-container),
.mobile-vocab-sheet-body :deep(.p-carousel-items-content),
.mobile-vocab-sheet-body :deep(.p-carousel-items-container) {
    width: 100% !important;
    box-sizing: border-box;
}

.mobile-vocab-sheet-body :deep(.p-carousel-item) {
    width: 100% !important;
    flex-shrink: 0;
    box-sizing: border-box;
}

.mobile-vocab-card {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--color-primary-mid, #e0e0e0);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 12px;
    background: white;
}

.mobile-vocab-item + .mobile-vocab-item {
    border-top: 1px solid #f0f0f0;
    margin-top: 10px;
    padding-top: 10px;
}

.mobile-vocab-word-row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
}

.mobile-vocab-word {
    font-size: 18px;
    font-weight: 700;
    color: var(--color-primary, #333);
}

.mobile-vocab-reading {
    font-size: 13px;
    font-family: monospace;
    color: #7c5cbf;
}

.mobile-vocab-jlpt {
    font-size: 11px;
    background: #fff3cd;
    color: #856404;
    border-radius: 4px;
    padding: 1px 6px;
    font-weight: 600;
}

.mobile-vocab-meaning {
    font-size: 14px;
    color: #444;
    margin: 0;
    line-height: 1.5;
}

.mobile-vocab-save-btn {
    margin-left: auto;
    background: none;
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 4px 8px;
    cursor: pointer;
    color: #666;
    font-size: 15px;
    line-height: 1;
    transition: color 0.2s, border-color 0.2s;
}

.mobile-vocab-save-btn.saved {
    color: var(--color-primary, #454aaa);
    border-color: var(--color-primary, #454aaa);
}

/* Mobile Responsive Media Query */
@media (max-width: 599px) {
    .desktop-view {
        display: none !important;
    }

    .mobile-view {
        display: flex;
    }

    .page-inner-content {
        padding-left: 0;
    }
}
</style>